<template>
    <div class="apply-bootstrap">
        <div v-for="(bridgeComponent, index) in bridgeComponents" :key="index">
            <teleport :to="bridgeComponent.to" v-if="bridgeComponent.cType && bridgeComponent.cId && !bridgeComponent.unmounted">
                <component
                    :is="bridgeComponent.cType"
                    :cId="bridgeComponent.cId"
                    v-bind="bridgeComponent.attrs"
                    :ref="bridgeComponent.cId"
                    @componentMounted="componentMounted"
                ></component>
            </teleport>
        </div>
        <div v-for="(bridgeControl, index) in bridgeControls" :key="index">
            <teleport :to="bridgeControl.to" v-if="bridgeControl.name && bridgeControl.config && !bridgeControl.unmounted">
                <control :name="bridgeControl.name" :prop-config="bridgeControl.config" v-bind="bridgeControl.attrs"></control>
            </teleport>
        </div>
    </div>
</template>
<script>
import { defineAsyncComponent } from 'vue';
import { store } from "@common/store/store";
import { errorHandler } from "@common/errorHandler/errorHandler";
import "@common/syncfusionCss";
import { componentStore, componentGlobalMixins } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { uStore } from "@common/userStore";

let inlineComponents = [];
let mountTimeout = false;

const vueBridgeCmp = {
    name: 'vueBridge',
    data() {
        return {
            bridgeComponents: {},
            bridgeControls: {}
        };
    },
    mounted() {
        uStore().getUserData('user');
        this.mountComponents();
        this.mountControls();
        let target = document.querySelector('body');
        if (target) {
            let observer = new MutationObserver((mutations) => {
                for (let i in mutations) {
                    if (mutations[i].removedNodes) {
                        for (let j in mutations[i].removedNodes) {
                            if (
                                mutations[i].removedNodes[j]
                                && mutations[i].removedNodes[j].attributes
                                && mutations[i].removedNodes[j].attributes['c-id']
                                && mutations[i].removedNodes[j].attributes['c-id'].value
                            ) {
                                this.unmountComponent(mutations[i].removedNodes[j].attributes['c-id'].value);
                            }
                            if (
                                mutations[i].removedNodes[j]
                                && mutations[i].removedNodes[j]?.tagName?.toLowerCase() === 'control'
                            ) {
                                this.unmountControls(mutations[i].removedNodes[j].attributes['name'].value);
                            }
                        }
                    }
                }
                if (mountTimeout) clearTimeout(mountTimeout);
                mountTimeout = setTimeout(() => {
                    // try decrease calls
                    this.mountComponents();
                    this.mountControls();
                }, 500);
            });
            observer.observe(target, {
                subtree: true,
                childList: true
            });
        }
        // eslint-disable-next-line no-new-func
        let oldOnload = window?.onload || new Function();
        window.onload = () => {
            this.mountComponents();
            this.mountControls();
            oldOnload();
        };
    },
    methods: {
        unmountComponent(cId) {
            if (this.bridgeComponents && this.bridgeComponents[cId]) {
                this.bridgeComponents[cId].unmounted = true;
                delete this.bridgeComponents[cId];
                delete window.vueRefs[cId];
            }
        },
        unmountControls(name) {
            if (this.bridgeControls && this.bridgeControls[name]) {
                this.bridgeControls[name].unmounted = true;
                delete this.bridgeControls[name];
            }
        },
        mountComponents(forceComponent) {
            let components = forceComponent ? [forceComponent] : document.querySelectorAll('[c-id]:not([vue-component-mounted]):not([vue-component-has-visibility-observer])');
            let awailableComponentTypes = library.cloneObj(componentStore.getTypes());
            awailableComponentTypes = awailableComponentTypes.map((tmpCType) => tmpCType.toLowerCase());
            for (let i = 0; i < components.length; i++) {
                let tmpComponent = components[i];
                let componentShouldBeMounted = true;
                if (typeof tmpComponent.checkVisibility === 'function' && !tmpComponent.checkVisibility()) {
                    componentShouldBeMounted = false;
                    if (!components[i].getAttribute('vue-component-has-visibility-observer')) {
                        components[i].setAttribute('vue-component-has-visibility-observer', true);
                        let observer = new MutationObserver((mutations) => {
                            if (mutations?.[0]?.target?.checkVisibility?.()) {
                                observer.disconnect();
                                this.mountComponents(tmpComponent);
                            }
                        });
                        observer.observe(tmpComponent, {
                            attributes: true
                        });
                        library.onElementVisible(tmpComponent, () => this.mountComponents(tmpComponent));
                    }
                }
                if (typeof tmpComponent.closest === 'function' && tmpComponent.closest('#vueBridge')) componentShouldBeMounted = false;
                /* while (tmpComponent) {
                    if (tmpComponent.attributes && tmpComponent.attributes.id && tmpComponent.attributes.id.value === 'vueBridge') {
                        componentShouldBeMounted = false;
                        break;
                    }
                    tmpComponent = tmpComponent.parentNode;
                } */
                if (componentShouldBeMounted) {
                    let cType = components[i].tagName.toLowerCase();
                    let cId = components[i].attributes['c-id'].value;
                    let attrs = {};
                    components[i].setAttribute('vue-component-mounted', true);
                    if (awailableComponentTypes.indexOf(cType) === -1) components[i].setAttribute('vue-component-not-found', true);
                    for (let j = 0; j < components[i].attributes.length; j++) {
                        let value = components[i].attributes[j].value;
                        let propName = components[i].attributes[j].name;
                        let parsedPropName = library.camelCase(propName);
                        if (propName === 'c-id' && i > 0) {
                            components[i].attributes[j].value += '__' + i;
                            cId = components[i].attributes[j].value;
                        }
                        if ((propName.indexOf('c-id') === -1 || propName.indexOf('acc-id') !== -1) && propName !== 'style') {
                            if (!isNaN(parseInt(value, 10)) && /^\d+$/.test(value)) {
                                value = parseInt(value, 10);
                            } else if (value === 'true' || value === 'false') {
                                value = value === 'true';
                            }
                            let inGlobalMixins = typeof componentGlobalMixins.props[parsedPropName] !== 'undefined';
                            if (!propName.startsWith('prop-') && !inGlobalMixins) propName = 'prop-' + propName;
                            attrs[propName] = value;
                        }
                    }
                    if (typeof attrs['class'] === 'undefined') attrs['class'] = '';
                    attrs['class'] += ' apply-bootstrap ';
                    this.bridgeComponents[cId] = {
                        to: components[i],
                        cId: cId,
                        cType: cType,
                        attrs: attrs
                    };
                }
            }
        },
        mountControls() {
            let controls = document.querySelectorAll('control[name][config]:not([vue-control-mounted])');
            for (let i = 0; i < controls.length; i++) {
                let tmpControl = controls[i];
                let controlShouldBeMounted = true;
                while (tmpControl) {
                    if (tmpControl.attributes) {
                        if (tmpControl.attributes.id && tmpControl.attributes.id.value === 'vueBridge') {
                            controlShouldBeMounted = false;
                            break;
                        } else if (tmpControl.attributes['c-id']) {
                            controlShouldBeMounted = false;
                            break;
                        }
                    }
                    tmpControl = tmpControl.parentNode;
                }
                if (controlShouldBeMounted) {
                    let name = controls[i].attributes['name'].value;
                    let config = controls[i].attributes['config'].value;
                    if (!config || !library.isJson(config)) continue;
                    config = JSON.parse(config);
                    let attrs = {};
                    controls[i].setAttribute('vue-control-mounted', true);
                    for (let j = 0; j < controls[i].attributes.length; j++) {
                        let value = controls[i].attributes[j].value;
                        let propName = controls[i].attributes[j].name;
                        if (propName === 'name' && i > 0) {
                            controls[i].attributes[j].value += '__' + i;
                            name = controls[i].attributes[j].value;
                        }
                        if (propName === 'config' || propName === 'name') continue;
                        attrs[propName] = value;
                    }
                    if (typeof attrs['class'] === 'undefined') attrs['class'] = '';
                    attrs['class'] += ' apply-bootstrap ';
                    this.bridgeControls[name] = {
                        to: controls[i],
                        name: name,
                        config: config,
                        attrs: attrs
                    };
                }
            }
        },
        componentMounted(cId) {
            if (typeof window.vueRefs === 'undefined') window.vueRefs = [];
            if (this.$refs && this.$refs[cId] && this.$refs[cId][0] && typeof this.$refs[cId][0].manualSave === 'function') {
                window.vueRefs[this.$refs[cId][0].cId] = this.$refs[cId][0];
            }
        }
    },
    components: {
        store,
        errorHandler,
        layoutContainer: defineAsyncComponent(() => import('@common/layoutContainer/layoutContainer.vue')),
        control: defineAsyncComponent(() => import('@common/control/control.vue'))
    }
};

window.vueBridgeCmp = vueBridgeCmp;
export default vueBridgeCmp;
</script>

<style lang="css" scoped>
@import "~@common/global.css";
@import "~@assets/css/utilities.css";
@import "~@assets/css/fonts.css";
</style>
